import { BROKER_FILTER_OPTIONS, DO_NOT_CALL_FILTER_OPTIONS, TriStateFilterValue } from '@entities/clients';
import { FilterProject } from '@shared/api/filters-ws';
import { Select, SelectOption, SelectValue } from '@shared/ui/select';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { Input } from '@shared/ui/kit';
import { FilterRadioGroup } from './FilterRadioGroup';
import { DealsSummFilter, EMPTY_DEALS_SUMM, isDealsSummRangeValid } from './dealsSumm';
import styles from './filter-clients.module.scss';
import { CrossIcon } from '@shared/assets/icons';
import { useMemo } from 'react';
import clsx from 'clsx';

interface FilterClientsProps {
  projects: FilterProject[];
  projectCodes: SelectValue[];
  onProjectCodesChange: (value: SelectValue[]) => void;
  dealsSumm: DealsSummFilter;
  onDealsSummChange: (value: DealsSummFilter) => void;
  hasBroker: TriStateFilterValue;
  onHasBrokerChange: (value: TriStateFilterValue) => void;
  doNotCall: TriStateFilterValue;
  onDoNotCallChange: (value: TriStateFilterValue) => void;
  isDisabled?: boolean;
}

export const FilterClients = ({
  projects,
  projectCodes,
  onProjectCodesChange,
  dealsSumm,
  onDealsSummChange,
  hasBroker,
  onHasBrokerChange,
  doNotCall,
  onDoNotCallChange,
  isDisabled,
}: FilterClientsProps) => {
  const projectsOptions = useMemo(() => {
    const codes = new Set<string>();
    const options: SelectOption[] = [];

    projects.forEach(({ code, name }) => {
      if (!code || codes.has(code)) return;
      codes.add(code);
      options.push({ value: code, name: name || code });
    });

    return options;
  }, [projects]);

  const activeFiltersCount = [
    projectCodes.length,
    dealsSumm.from || dealsSumm.to,
    hasBroker !== null,
    doNotCall !== null,
  ].filter(Boolean).length;

  const isDealsSummInvalid = !isDealsSummRangeValid(dealsSumm);
  const dealsSummStatus = isDealsSummInvalid ? 'error' : 'default';
  const isFilterEmpty = !activeFiltersCount;

  const onResetFilter = () => {
    onDealsSummChange(EMPTY_DEALS_SUMM);
    onProjectCodesChange([]);
    onHasBrokerChange(null);
    onDoNotCallChange(null);
  };

  return (
    <div className={clsx(styles.container, isDisabled && styles.disabled)}>
      <div className={styles.header}>
        <div className={styles.title}>Фильтры {!!activeFiltersCount && <span>{activeFiltersCount}</span>}</div>

        <UnstyledButton
          className={clsx(styles.resetButton, !isFilterEmpty && styles.resetButtonActive)}
          onClick={onResetFilter}
          disabled={isFilterEmpty || isDisabled}
        >
          Сбросить
          <CrossIcon color="currentColor" />
        </UnstyledButton>
      </div>

      <div className={styles.clients}>
        <div className={styles.filterGroup}>
          <div className={styles.filterLabel}>Оборот клиента, млн ₽</div>
          <div className={styles.dealsSummRow}>
            <Input
              type="number"
              size="small"
              labelText="От"
              placeholder="млн"
              status={dealsSummStatus}
              value={dealsSumm.from}
              changeFunction={(from: string) => onDealsSummChange({ ...dealsSumm, from })}
              isDisabled={isDisabled}
              settings={{ width: '100%' }}
            />
            <Input
              type="number"
              size="small"
              labelText="До"
              placeholder="млн"
              status={dealsSummStatus}
              value={dealsSumm.to}
              changeFunction={(to: string) => onDealsSummChange({ ...dealsSumm, to })}
              isDisabled={isDisabled}
              settings={{ width: '100%' }}
            />
          </div>
          {isDealsSummInvalid && <div className={styles.dealsSummError}>«От» не может быть больше «До»</div>}
        </div>

        <Select
          label="Проекты"
          placeholder="Выберите проект"
          options={projectsOptions}
          selectedValue={projectCodes}
          onChange={(values) => Array.isArray(values) && onProjectCodesChange(values)}
          isWithChips
          isDisabled={isDisabled}
          selectAllLabel="Выбрать все"
          listedChipsVariant="brandGhost"
        />

        <div className={styles.radioFilters}>
          <FilterRadioGroup
            label="Наличие брокера"
            value={hasBroker}
            onChange={onHasBrokerChange}
            options={BROKER_FILTER_OPTIONS}
            isDisabled={isDisabled}
          />

          <FilterRadioGroup
            label="Отказ от звонков"
            value={doNotCall}
            onChange={onDoNotCallChange}
            options={DO_NOT_CALL_FILTER_OPTIONS}
            isDisabled={isDisabled}
          />
        </div>
      </div>
    </div>
  );
};
